import React, { useState } from 'react';
import { readTrust, saveTrust } from '@/lib/mfa-trust';
import { GoogleAuthButton, OrRule } from '@/components/auth/google-auth-button';
import { LinkedInAuthButton, useSigninProviders } from '@/components/auth/linkedin-auth-button';
import { Check, CheckCircle2, Copy, Download, ExternalLink, Loader2 } from 'lucide-react';

import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MMLogoMark } from '@/components/brand/mm-logo';
import { LoginBee } from '@/components/brand/login-bee';
import { CookieBeesWordmark } from '@/components/brand/cookiebees-wordmark';
import { LoginShowcase } from '@/components/auth/login-showcase';
import { startMemberSession } from '@/lib/agencyMember';

// Another page finished the first factor (Google sign-in, the verify-email
// link) and was handed a challenge instead of a session. It parks it here and
// sends the person to the login page, which picks it up on mount - so there is
// exactly one place that draws the code and enrolment screens.
// `note` is what just happened ("Email confirmed."), said above the screen so
// a brand-new client knows they are one step from their workspace, not
// bounced to a sign-in page.
export const CHALLENGE_KEY = 'cb_mfa_challenge';
export function parkChallenge(data, { note = null } = {}) {
  try { sessionStorage.setItem(CHALLENGE_KEY, JSON.stringify({ token: data.mfa_token, setup: !!data.mfa_setup_required, note })); } catch {}
}

// One button that copies, and says so for a moment.
function CopyButton({ text, label, className }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    let ok = false;
    try { await navigator.clipboard.writeText(text); ok = true; } catch {
      // No clipboard API (older phone browsers, http): copy through a
      // selected field. The text stays on screen either way.
      try {
        const t = document.createElement('textarea');
        t.value = text; t.setAttribute('readonly', ''); t.style.position = 'fixed'; t.style.opacity = '0';
        document.body.appendChild(t); t.select(); ok = document.execCommand('copy'); t.remove();
      } catch { ok = false; }
    }
    if (ok) { setDone(true); setTimeout(() => setDone(false), 1800); }
  };
  return (
    <Button type="button" variant="outline" size="sm" onClick={copy} className={className}>
      {done ? <Check className="size-4" /> : <Copy className="size-4" />}{done ? 'Copied' : label}
    </Button>
  );
}

export default function Login({ onLogin }) {
  // Which provider doors this server has (Google always; LinkedIn once its app is set).
  const providers = useSigninProviders();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resent, setResent] = useState(false);
  const [mode, setMode] = useState('login');   // 'login' | 'forgot' | 'mfa'
  const [forgotSent, setForgotSent] = useState(false);
  // Two-step verification: the password was right, and this is the challenge
  // the server handed back instead of a session.
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  // Remember this browser for 30 days after a correct code (owner, 8 Oct 2026:
  // clients were asked for the code on every sign-in). On by default; a
  // shared computer can untick it.
  const [remember, setRemember] = useState(true);
  // Enrolling mid-login, because this workspace requires two-step verification
  // and this person has not set it up yet.
  const [enrol, setEnrol] = useState(null);        // { qr_data_url, secret }
  const [enrolCodes, setEnrolCodes] = useState(null);  // recovery codes, shown once
  const [pendingUser, setPendingUser] = useState(null);
  const [note, setNote] = useState(null);

  // A challenge parked by another door.
  React.useEffect(() => {
    let parked = null;
    try { parked = JSON.parse(sessionStorage.getItem(CHALLENGE_KEY) || 'null'); sessionStorage.removeItem(CHALLENGE_KEY); } catch {}
    if (!parked?.token) return;
    setMfaToken(parked.token);
    setMfaCode('');
    setNote(parked.note || null);
    if (!parked.setup) { setMode('mfa'); return; }
    setMode('mfa-setup');
    api.post('/api/auth/mfa/enrol/start', { mfa_token: parked.token })
      .then(({ data: st }) => setEnrol(st))
      .catch((e2) => { setError(e2.response?.data?.error || 'Could not start setup. Sign in again.'); setMode('login'); });
  }, []);

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await api.post('/api/auth/forgot-password', { email });
      setForgotSent(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setNeedsVerify(false);

    try {
      const { data } = await api.post('/api/auth/login', { email, password, mfa_trust: readTrust() });

      // Two-step verification: the password was accepted but this is not a
      // session yet. Ask for the code; nothing is stored until it checks out.
      if (data.mfa_required) {
        setMfaToken(data.mfa_token);
        setMfaCode('');
        setMode('mfa');
        setLoading(false);
        return;
      }

      // The workspace requires two-step verification and this person has none
      // yet: enrol here, now, rather than bouncing them off a locked door.
      if (data.mfa_setup_required) {
        setMfaToken(data.mfa_token);
        setMfaCode('');
        setMode('mfa-setup');
        try {
          const { data: s } = await api.post('/api/auth/mfa/enrol/start', { mfa_token: data.mfa_token });
          setEnrol(s);
        } catch (e2) {
          setError(e2.response?.data?.error || 'Could not start setup. Sign in again.');
          setMode('login');
        }
        setLoading(false);
        return;
      }

      // An agency member (one login across workspaces) lands in the Agency
      // Console instead of a workspace; enterSession knows the difference.
      enterSession(data);
    } catch (err) {
      if (err.response?.data?.code === 'email_unverified') {
        setNeedsVerify(true);
        setError('Please verify your email before signing in.');
      } else {
        setError(err.response?.data?.error || 'Invalid email or password');
      }
    } finally {
      setLoading(false);
    }
  };

  // One place that turns a successful answer into a session, so the code step
  // and the password step cannot drift apart.
  const enterSession = (data) => {
    if (data.agency_session) {
      startMemberSession(data.token, data.member);
      window.location.assign('/app');
      return;
    }
    localStorage.setItem('token', data.token);
    localStorage.setItem('userId', data.user.id);
    localStorage.setItem('userName', data.user.name);
    onLogin(data.user);
  };

  const handleEnrol = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/api/auth/mfa/enrol/finish', { mfa_token: mfaToken, code: mfaCode, remember });
      if (data.mfa_trust) saveTrust(data.mfa_trust);
      // The recovery codes exist in readable form exactly once, and this is it.
      // Hold the session until they have been acknowledged.
      setEnrolCodes(data.recovery_codes || []);
      setPendingUser(data);
    } catch (err) {
      if (err.response?.data?.code === 'mfa_expired') { setMode('login'); setMfaToken(''); setEnrol(null); }
      setError(err.response?.data?.error || 'That code is not right.');
      setMfaCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleMfa = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/api/auth/mfa/verify', { mfa_token: mfaToken, code: mfaCode, remember });
      if (data.mfa_trust) saveTrust(data.mfa_trust);
      enterSession(data);
    } catch (err) {
      if (err.response?.data?.code === 'mfa_expired') {
        setMode('login');
        setMfaToken('');
      }
      setError(err.response?.data?.error || 'That code is not right.');
      setMfaCode('');
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    try { await api.post('/api/auth/resend-verification', { email }); setResent(true); } catch {}
  };


  return (
    <div className="relative grid min-h-screen w-full place-items-center overflow-hidden bg-background bg-gradient-to-b from-honey-50/80 via-background to-background px-4 py-10 dark:from-honey-950/25">
      {/* No background blobs (owner, 25 Oct 2026: "too many color spots"),
          and not flat either ("very dull"): one faint honey wash from the top
          of the page, honey and ink everywhere else. */}
      <LoginBee />

      {/* TWO PANELS. Left: what Cookiebees is, in the owner's chosen words
          (4 Oct 2026) - a headline, one sentence and three proof points named
          for what the product does. Right: the form, unchanged in behaviour.
          The sentence names the app and its purpose, which Google's OAuth
          branding reviewers look for on this page. On a phone the proof
          points drop away and the form follows the headline. */}
      <div className="relative z-10 grid w-full max-w-6xl items-center gap-8 lg:grid-cols-[minmax(0,1.12fr)_minmax(0,1fr)] lg:gap-14">
        <div className="mx-auto w-full max-w-md lg:mx-0 lg:max-w-none">
          {/* The bee mark beside the wordmark: one brand lockup, no second
              name inside the card. */}
          <div className="flex items-center justify-center gap-3 lg:justify-start">
            <MMLogoMark size={52} cookieHead />
            <CookieBeesWordmark className="h-7 w-auto text-zinc-900 dark:text-zinc-50" />
          </div>
          <LoginShowcase />
        </div>

        <div className="mx-auto w-full max-w-md">
        <Card className="shadow-floating">
          <CardContent className="p-8">
            {mode === 'mfa-setup' ? (
              enrolCodes ? (
                <>
                  <div className="mb-5">
                    <h1 className="text-xl font-semibold tracking-tight">Save your recovery codes</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Each one signs you in once if you lose your phone. They are stored hashed,
                      so this is the only time they can be shown - not by us either.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1 rounded-md border border-amber-500/40 bg-amber-500/[0.06] p-4 font-mono text-[13px]">
                    {enrolCodes.map((c) => <span key={c}>{c}</span>)}
                  </div>
                  {/* On a phone there was no way to keep them but a screenshot. */}
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <CopyButton text={enrolCodes.join('\n')} label="Copy codes" />
                    <Button type="button" variant="outline" size="sm" asChild>
                      <a download="cookiebees-recovery-codes.txt"
                        href={`data:text/plain;charset=utf-8,${encodeURIComponent(`Cookiebees recovery codes\nEach one signs you in once.\n\n${enrolCodes.join('\n')}\n`)}`}>
                        <Download className="size-4" />Download
                      </a>
                    </Button>
                  </div>
                  <Button
                    className="mt-4 w-full"
                    onClick={() => enterSession(pendingUser)}
                  >
                    I have saved them - continue
                  </Button>
                </>
              ) : (
                <>
                  <div className="mb-5">
                    {note && (
                      <p className="mb-3 flex items-center gap-2 rounded-md bg-brand-50 px-3 py-2 text-[13.5px] text-brand-800">
                        <CheckCircle2 className="size-4 shrink-0 text-brand-700" />
                        {note} One last step, then your workspace opens.
                      </p>
                    )}
                    <h1 className="text-xl font-semibold tracking-tight">Set up two-step verification</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Every Cookiebees login uses a second factor. Scan the code with an authenticator
                      app (Google Authenticator, Microsoft Authenticator, 1Password), then type the 6-digit number it shows.
                    </p>
                  </div>
                  {enrol ? (
                    <form onSubmit={handleEnrol} className="space-y-4">
                      <div className="flex justify-center">
                        <img
                          src={enrol.qr_data_url}
                          alt="Scan this with your authenticator app"
                          width={180}
                          height={180}
                          className="rounded-md border border-border bg-white p-2"
                        />
                      </div>
                      {/* On a phone there is nothing to scan: the code is on the
                          phone's own screen. The otpauth link opens the
                          authenticator app with the account filled in. */}
                      {enrol.otpauth_url && (
                        <Button type="button" variant="outline" className="h-auto min-h-10 w-full whitespace-normal py-2 lg:hidden" asChild>
                          <a href={enrol.otpauth_url}><ExternalLink className="size-4 shrink-0" />Open in authenticator app</a>
                        </Button>
                      )}
                      <div className="space-y-1">
                        <div className="text-[13px] font-medium first-letter:uppercase text-muted-foreground">
                          Can&apos;t scan? Type this key
                        </div>
                        <div className="flex items-center gap-2">
                          <code className="block min-w-0 flex-1 break-all rounded bg-muted/60 px-2 py-1 font-mono text-[12px]">
                            {enrol.secret}
                          </code>
                          <CopyButton text={enrol.secret} label="Copy" className="shrink-0" />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="enrol-code">6-digit code</Label>
                        <Input
                          id="enrol-code"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          placeholder="123456"
                          value={mfaCode}
                          onChange={(ev) => setMfaCode(ev.target.value.trim())}
                          autoFocus
                          required
                          disabled={loading}
                          className="text-center text-lg tracking-[0.35em]"
                        />
                      </div>
                      {error && (
                        <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">{error}</p>
                      )}
                      <Button type="submit" className="w-full" disabled={loading || mfaCode.length < 6}>
                        {loading && <Loader2 className="size-4 animate-spin" />}
                        Turn on and sign in
                      </Button>
                      <button
                        type="button"
                        onClick={() => { setMode('login'); setMfaToken(''); setEnrol(null); setMfaCode(''); setError(''); setPassword(''); }}
                        className="w-full text-center text-sm text-muted-foreground hover:text-foreground"
                      >
                        Back to sign in
                      </button>
                    </form>
                  ) : (
                    <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                      <Loader2 className="mr-2 size-4 animate-spin" /> Preparing your code…
                    </div>
                  )}
                </>
              )
            ) : mode === 'mfa' ? (
              <>
                <div className="mb-6">
                  <h1 className="text-xl font-semibold tracking-tight">Enter your code</h1>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Open your authenticator app and type the 6-digit code for
                    <span className="font-medium text-foreground"> Cookiebees</span>. It changes every 30 seconds.
                  </p>
                </div>
                <form onSubmit={handleMfa} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="mfa-code">6-digit code</Label>
                    <Input
                      id="mfa-code"
                      // A numeric one-time code: the phone keyboard should be
                      // digits, and the browser/iOS should offer to fill it.
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="123456"
                      value={mfaCode}
                      onChange={(ev) => setMfaCode(ev.target.value.trim())}
                      autoFocus
                      required
                      disabled={loading}
                      className="text-center text-lg tracking-[0.35em]"
                    />
                    <p className="text-xs text-muted-foreground">
                      Lost the phone? Type one of your recovery codes here instead.
                    </p>
                  </div>
                  <label htmlFor="mfa-remember" className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
                    <input id="mfa-remember" type="checkbox" checked={remember} onChange={(ev) => setRemember(ev.target.checked)}
                      className="size-4 rounded border-border accent-[hsl(var(--primary))]" />
                    Remember this device for 30 days
                  </label>
                  {error && (
                    <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">{error}</p>
                  )}
                  <Button type="submit" className="w-full" disabled={loading || !mfaCode}>
                    {loading && <Loader2 className="size-4 animate-spin" />}
                    Verify and sign in
                  </Button>
                  <button
                    type="button"
                    onClick={() => { setMode('login'); setMfaToken(''); setMfaCode(''); setError(''); setPassword(''); }}
                    className="w-full text-center text-sm text-muted-foreground hover:text-foreground"
                  >
                    Back to sign in
                  </button>
                </form>
              </>
            ) : mode === 'forgot' ? (
              forgotSent ? (
                <div className="space-y-3 text-center">
                  <h1 className="text-lg font-semibold">Check your email</h1>
                  <p className="text-sm text-muted-foreground">
                    If <span className="font-medium text-foreground">{email}</span> has an account, a reset link is on its way. It expires in 1 hour.
                  </p>
                  <button type="button" onClick={() => { setMode('login'); setForgotSent(false); setError(''); }} className="text-sm text-primary hover:underline">
                    Back to sign in
                  </button>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <h1 className="text-xl font-semibold tracking-tight">Reset your password</h1>
                    <p className="mt-1 text-sm text-muted-foreground">Enter your email and we&apos;ll send you a reset link.</p>
                  </div>
                  <form onSubmit={handleForgot} className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="fmail">Email</Label>
                      <Input id="fmail" type="email" autoComplete="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={loading} />
                    </div>
                    {error && (
                      <div role="alert" className="rounded-md border border-destructive-soft bg-destructive-soft/60 px-3 py-2 text-sm text-destructive">{error}</div>
                    )}
                    <Button type="submit" disabled={loading || !email} className="w-full">
                      {loading ? <><Loader2 className="size-4 animate-spin" /> Sending…</> : 'Send reset link'}
                    </Button>
                    <button type="button" onClick={() => { setMode('login'); setError(''); }} className="block w-full text-center text-sm text-primary hover:underline">
                      Back to sign in
                    </button>
                  </form>
                </>
              )
            ) : (
            <>
            <div className="mb-6">
              <h1 className="text-xl font-semibold tracking-tight">
                Sign in
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                to your Cookiebees workspace
              </p>
            </div>

            {/* Google first: for anyone whose workspace was made with it, this
                is the whole sign-in. The password form stays for everyone else. */}
            <div className="space-y-4">
              <GoogleAuthButton label="Continue with Google" onError={setError} />
              {providers?.linkedin && <LinkedInAuthButton onError={setError} />}
              <OrRule text="or sign in with email" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <button type="button" onClick={() => { setMode('forgot'); setError(''); }} className="text-xs text-primary hover:underline">
                    Forgot password?
                  </button>
                </div>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>

              {error && (
                <div
                  role="alert"
                  className="rounded-md border border-destructive-soft bg-destructive-soft/60 px-3 py-2 text-sm text-destructive"
                >
                  {error}
                  {needsVerify && (
                    <div className="mt-1.5 text-foreground">
                      {resent ? (
                        <span className="text-primary">Verification email re-sent.</span>
                      ) : (
                        <button type="button" onClick={resendVerification} className="text-primary hover:underline">
                          Resend verification email
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || !email || !password}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  'Sign in'
                )}
              </Button>
            </form>
            </>
            )}

            {/* For someone without an account. Not under the two-step screens:
                whoever is there has one (often made a minute ago). */}
            {mode !== 'mfa' && mode !== 'mfa-setup' && (
              <div className="mt-6 border-t pt-4 text-center text-xs text-muted-foreground">
                New to Cookiebees? <a href="/app/signup" className="text-primary hover:underline">Start your 30-day free trial</a>
              </div>
            )}
          </CardContent>
        </Card>

        </div>

        {/* What a visitor looks for at the foot of a sign-in page: who, and
            the two documents. */}
        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground lg:col-span-2">
          <span>© {new Date().getFullYear()} Cookiebees</span>
          <span aria-hidden="true">·</span>
          <a href="https://cookiebees.io/privacy" className="hover:text-foreground hover:underline">Privacy</a>
          <span aria-hidden="true">·</span>
          <a href="https://cookiebees.io/terms" className="hover:text-foreground hover:underline">Terms</a>
        </div>
      </div>
    </div>
  );
}
